<script>
import { SelectField, SelectMultipleField, ToggleButton } from '@/components/form';
import { useAuthStore } from '@/stores/auth';
import { CommunitiesController } from '@/controllers/communities';
import { mapActions, mapState } from 'pinia';
import axios from 'axios';
import MapView from '@/components/MapView.vue';
import colors from '@/helpers/colors';
import { useMessageStore } from '@/stores/message';
import MessageDisplay from '@/components/MessageDisplay.vue';
import { formatToCurrency, formatToNumber } from '@/helpers/numberHelpers';
import FormButton from '@/components/form/FormButton.vue';
import SalesOrderCard from '@/components/SalesOrderCard.vue';

export default {
    setup() {
        return {
            formatToCurrency,
            formatToNumber
        }
    },
    data(){
        return {
            communities: [],
            filters: {
                community: this.community,
                collections: this.collection_ids,
                pois: true
            },
            collections: [],
            parcels: [],
            loading: false,
            lot: null,
            sales_order: null,
            iframeLoading: true
        }
    },
    methods: {
        ...mapActions(useMessageStore, ['clear']),
        close() {
            this.lot = null; 
            this.sales_order = null; 
            this.clear();
        },
        indexCommunities() {
            CommunitiesController.index()
            .then(resp => {
                this.communities = resp.filter(community => community.status === 'active');
            })
            .catch(error => {
                console.error(error)
            })
        },
        indexCollections() {
            if(!this.filters.community) return;
            axios.get(`/divisions/${this.filters.community}/collections`)
            .then(resp => {
                this.collections = resp.data
                    .filter(collection => collection.status === 'active');
                this.filters.collections = this.collections
                    .filter(collection => 
                        (this.collection_ids.length && this.collection_ids.includes(collection.id)) || !this.collection_ids.length
                    )
                    .map(collection => collection.id)
            })
            .catch(err => {
                console.error(err)
            })
        },
        indexParcels() {
            this.loading = true;
            axios.get('parcels')
                .then(resp => {
                    this.parcels = resp.data;
                    this.loading = false;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        setCollectons() {
            if(this.filters.collections.length === this.collections.length) {
                this.$router.push({name: 'map-community', params: { community: this.filters.community }});
            } else if(this.filters.collections.length) {
                this.$router.push({name: 'map-collections', params: { community: this.filters.community.id, collections: this.filters.collections.join(',') }});
            }
        },
        setLot(feature) {
            if(feature?.properties?.lot_id ?? false) {
                this.lot = feature.properties.lot_id;
                this.loading = true;
                axios.get(`lots/${this.lot}/sales_order`)
                    .then(resp => {
                        this.sales_order = resp.data;
                    })
                    .catch(error => {
                        console.error(error);
                        this.sales_order = false;
                    })
                    .finally(() => {
                        this.loading = false;
                    })
                return;
            }
            this.lot = null;
            this.sales_order = null
        },
        iframeLoaded() {
            this.iframeLoading = false;
        }
    },
    computed: {
        ...mapState(useAuthStore, ['authenticated']),
        ...mapState(useMessageStore, ['message']),
        listCommunities() {
            return [
                ...this.communities.map(community => ({label: community.meta?.info?.name, value: community.id}))
            ]
        },
        listCollections() {
            return this.collections.map(collection => ({label: collection.meta?.info?.name, value: collection.id}))
        },
        filteredParcels() {
            let parcels = this.parcels;
            if(this.filters.community) {
                parcels = parcels.filter(parcel => parcel.community.id === this.filters.community)
            }
            if(this.filters.collections.length) {
                parcels = parcels.filter(parcel => this.filters.collections.includes(parcel.lot?.collection_id))
            }
            return parcels;
        },
        sources() {
            return [
                {
                    id: 'lots',
                    name: 'Lot Parcels',
                    features: this.filteredParcels.map(parcel => {
                        const { id, data, lot } = parcel;
                        return {
                            ...data,
                            id,
                            properties: {
                                ...data.properties,
                                lot: lot.lot_number ?? null,
                                block: lot.block_number ?? null,
                                id,
                                sales_order: lot?.sales_order?.status === 'active' ? lot.sales_order : undefined,
                                clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot.kova?.Status),
                                lot_id: lot.id ?? false,
                                status: lot?.kova?.Status ?? null,
                                type: lot?.kova?.Type ?? null
                            }
                        }
                    })
                },
                {
                    id: 'lot_points',
                    name: 'Lot Points',
                    features: this.filteredParcels
                        .map(parcel => {
                            const { id, lot, centroid } = parcel;

                            return {
                                type: "Feature",
                                geometry: {
                                    type: "Point",
                                    coordinates: centroid
                                },
                                properties: {
                                    id,
                                    sales_order: lot?.sales_order?.status === 'active' ? lot.sales_order : undefined,
                                    clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot?.kova?.Status),
                                    name: lot?.kova?.LotID,
                                    lot_rid: lot.lot_rid,
                                    lot_id: lot.id,
                                    status: lot?.kova?.Status ?? null,
                                    street: lot?.kova?.StreetAddress,
                                    city: lot?.kova?.City,
                                    state: lot?.kova?.State,
                                    zip: lot?.kova?.Zip,
                                    type: lot?.kova?.Type ?? null
                                }
                            }
                        }),
                    cluster: true,
                    clusterMaxZoom: 12,
                    clusterRadius: 100
                }
            ]
        },
        layers() {
            return [
                {
                    id: 'lot-base',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 13,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][100],
                            colors['bhav-blue'][50]
                        ],
                        'fill-opacity': 1,
                    },
                    before: 'lot-market',
                },
                {
                    id: 'lot-base-closed',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 13,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][100],
                            colors['bhav-blue'][50]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors['bhav-blue'][100],
                    },
                    before: 'lot-market',
                    filter: ['in', 'status', 'Closed',  'Available']
                },
                {
                    id: 'lot-market',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 13,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-green'][600],
                            colors['oakwood-green'][700]
                        ],
                        'fill-opacity': 1,
                    },
                    filter: [
                        'all',
                        ['has', 'sales_order'],
                        ['==', 'type', 'Market'],
                        ['in', 'status', 'New', 'Available']
                    ]
                },
                {
                    id: 'lot-model',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 13,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-orange'][400],
                            colors['oakwood-orange'][500]
                        ],
                        'fill-opacity': 1,
                    },
                    filter: [
                        'all',
                        ['==', 'type', 'Model'],
                        ['in', 'status', 'New', 'Available']
                    ]
                },
                {
                    id: 'lot-closed',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'closed-home-icon',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            1.2,
                            24,
                            4.8
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -2],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: ['==', 'status', 'Closed']
                },
                {
                    id: 'lot-sold',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'sold-home-icon',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            1.2,
                            24,
                            4.8
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -2],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: ['in', 'status', 'SoldContingent', 'Sold']
                },
                {
                    id: 'lot-boundaries',
                    type: 'line',
                    source: 'lots',
                    minzoom: 15,
                    paint: {
                        'line-color': colors['bhav-blue'][100],
                        'line-width': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            13, 0,
                            24, 4,
                        ]
                    },
                    before: 'lot-labels',
                    filter: [
                        'any',
                        [
                            'all',
                            ['has', 'sales_order'],
                            ['in', 'type', 'Model', 'Market'],
                            ['in', 'status', 'New', 'Available']
                        ],
                        [
                            'all',
                            ['in', 'status', 'Closed', 'Sold', 'SoldContingent'],
                        ]
                    ]
                },
                {
                    id: 'available-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'available-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -2],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: [
                        'all',
                        ['has', 'sales_order'],
                        ['==', 'type', 'Market'],
                        ['in', 'status', 'New', 'Available']
                    ]                },
                {
                    id: 'model-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'model-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -2],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: [
                        'all',
                        ['==', 'type', 'Model'],
                        ['in', 'status', 'New', 'Available']
                    ]
                },
                {
                    id: 'lot-labels',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'text-field': ['coalesce', ['get', 'street'], 'Unassigned'],
                        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                        'text-radial-offset': 0,
                        'text-justify': 'center',
                        'text-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            12,
                            24,
                            16
                        ],
                        'text-max-width': 25
                    },
                    minzoom: 18,
                    paint: {
                        'text-color': colors['oakwood-gray'][900],
                        'text-halo-color': colors['white'],
                        'text-halo-width': 2,
                        'text-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -12],
                            24,
                            [0, -30]
                        ],
                        'text-emissive-strength': 2
                    },
                    filter: [
                        'any',
                        [
                            'all',
                            ['has', 'sales_order'],
                            ['in', 'type', 'Model', 'Market'],
                            ['in', 'status', 'New', 'Available']
                        ],
                        [
                            'all',
                            ['in', 'status', 'Closed', 'Sold', 'SoldContingent'],
                        ]
                    ]
                },
                {
                    id: 'clusters',
                    type: 'circle',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': colors['bhav-blue'][700],
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            10,
                            100,
                            15,
                            300,
                            20,
                            500,
                            25
                        ],
                        'circle-emissive-strength': 1,
                        'circle-stroke-color': colors['white'],
                        'circle-stroke-width': 2
                    }
                },
                {
                    id: 'cluster-count',
                    type: 'symbol',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': ['get', 'point_count_abbreviated'],
                        'text-size': 12
                    },
                    paint: {
                        'text-color': colors['white']
                    }
                }
            ]
        }
    },
    components: {
        SelectField,
        SelectMultipleField,
        ToggleButton,
        MapView,
        SalesOrderCard,
        MessageDisplay,
        FormButton
    },
    props: {
        community: {
            type: [Number,null],
            default: null
        },
        collection_ids: {
            type: [Array],
            default: () => []
        }
    },
    created() {
        this.indexCommunities();
        this.indexParcels();
        if(this.community) {
            this.indexCollections();
        }
    },
    watch: {
        'filters.community': {
            handler(to,from) {
                if(to === from) return;
                if(to) {
                    this.$router.push({name: 'map-community', params: { community: this.filters.community }});
                    this.indexCollections();
                } else {
                    this.$router.push({name: 'map'});
                    this.collections = [];
                    this.filters.collections = [];
                }
            }
        }
    }
}
</script>

<template>
    <header class="fixed right-4 top-4 z-10">
        <h1 class="sr-only">Map View</h1>
        <nav v-if="authenticated" class="bg-white rounded-md px-2 py-1 shadow-lg">
            <router-link :to="{name: 'dashboard'}">
                <span class="fas fa-arrow-turn-down-left" title="Return to Dashboard"></span>
                <span class="sr-only">Return to Dashboard</span>
            </router-link>
        </nav>
    </header>
    <main>
        <section class="bg-oakwood-gray-100 w-full min-h-screen relative">
            <h2 class="sr-only">Map Display</h2>
            <MapView :sources="sources" :layers="layers" class="h-screen" :zoom="4" :padding="150" :loading="loading" :pois="filters.pois" @selected="setLot">
                <template #filters>
                    <SelectField label="Community" :options="listCommunities" v-model="filters.community" empty="None Selected..." />
                    <SelectMultipleField v-if="filters.community" label="Collections" :options="listCollections" v-model="filters.collections" @change="setCollectons" />
                    <div class="flex gap-2">
                        <ToggleButton id="pois" :checked="filters.pois" @click="filters.pois = !filters.pois" label="Points of Interest" />
                        <label for="pois" class="block text-sm font-medium leading-5 text-oakwood-gray-700">Points of Interest</label>
                    </div>
                </template>
                <template #legend>
                    <dl class="flex flex-col gap-2">
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-2">
                            <span title="Green"
                                class="inline-block w-8 h-8 border border-oakwood-gray-500 bg-bhav-green-600">
                                <img src="@/assets/images/available-home.svg" class="w-full aspect-square" />
                            </span>
                            Available Home
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-2">
                            <span title="Orange"
                                class="inline-block w-8 h-8 border border-oakwood-gray-500 bg-oakwood-orange-500">
                                <img src="@/assets/images/model-home.svg" class="w-full aspect-square" />
                            </span>
                            Model Home
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-2">
                            <span title="Red"
                                class="inline-block w-8 h-8 border border-oakwood-gray-500 bg-bhav-blue-50">
                                    <img src="@/assets/images/sold-home-icon.svg" class="w-full aspect-square" />
                            </span> Sold
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-2">
                            <span title="Red"
                                class="inline-block w-8 h-8 border border-oakwood-gray-500 bg-bhav-blue-50">
                                    <img src="@/assets/images/closed-home-icon.svg" class="w-full aspect-square" />
                            </span>
                            Closed
                        </dd>
                    </dl>
                </template>
            </MapView>
        </section>
        <SalesOrderCard v-if="lot" :loading="loading" @close="close" :url="sales_order?.meta?.info?.website ?? null" />
    </main>
</template>